import React, { useEffect, useState } from 'react'
import queryString from 'query-string'
import { t, Trans } from '@lingui/macro'
import { Link, Redirect, useLocation } from 'react-router-dom'
import { linkBuilder, staticLinks } from 'shared/linkBuilder'
import IconBan from '../../components/common/icon/IconBan'
import NotFound from '../../components/common/not-found/NotFound'
import { StudentTopicDetail } from './StudentTopicDetail'
import { getScheduledActionIdFromURL } from '../../utils/campaignTracking'
import {
  LastEnrolmentForUserDocument,
  useAddFreeQuizResponseMutation,
  useAddQuizResponseMutation,
  useOpenedFromCampaignShareEmailMutation,
  useOpenTopicEnrolmentQuery,
  useOpenTopicEnrolmentThroughPathwayQuery,
  useOpenTopicWithVersionQuery,
  useTopicDraftVersionQuery,
  useUpdateEnrolmentProgressMutation
} from './TopicDetailPage.codegen'
import './TopicDetailPage.less'
import { useHistory, useParams } from 'react-router'
import { IOptionResponse, IQuizResponse } from 'types/Topic'
import {
  TopicDetailContext,
  TopicDetailContextType
} from './TopicDetailContext'
import { apolloClientPrivate } from '../../graphql/apolloClient'
import { navigateTo } from '../../app/util/appHistory'
import { TopicVersionType } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { FullBlockSpinner } from 'front-end/src/components'

export enum TopicPageMode {
  PathwayView = 'PathwayView',
  WorkspaceView = 'WorkspaceView',
  Preview = 'Preview',
  Gradebook = 'Gradebook',
  VersionView = 'VersionView'
}

type RouteParams = {
  pathwayId?: string
  topicVersion?: string
  workspaceId?: string
  topicId: string
}

const TopicDetailPage = ({ mode }: { mode: TopicPageMode }) => {
  const location = useLocation()
  const history = useHistory()
  const params = useParams<RouteParams>()

  if (
    mode === TopicPageMode.VersionView &&
    (!params.topicVersion || !verifyTopicVersionParam(params.topicVersion))
  ) {
    return <Redirect to={linkBuilder.topicUrl(+params.topicId) as string} />
  }

  const topicId = Number(params.topicId)
  const pathwayId = Number(params.pathwayId)

  const [isLoading, setIsLoading] = useState(true)
  const [topic, setTopic] = useState<TopicDetailContextType['topic'] | null>(
    null
  )
  const [pathway, setPathway] =
    useState<TopicDetailContextType['pathway']>(null)
  const [nextInPathway, setNextInPathway] =
    useState<TopicDetailContextType['nextInPathway']>(null)

  const [addFreeQuizResponse] = useAddFreeQuizResponseMutation()
  const [addQuizResponse] = useAddQuizResponseMutation()
  const [updateEnrolmentProgress] = useUpdateEnrolmentProgressMutation()
  const [openedFromCampaign] = useOpenedFromCampaignShareEmailMutation()

  const { refetch: refetchTopicEnrolment } = useOpenTopicEnrolmentQuery({
    skip: mode !== TopicPageMode.WorkspaceView,
    variables: {
      topicId,
      restartEnrolment: false
    },
    fetchPolicy: 'network-only',
    nextFetchPolicy:
      topic?.currentTopicVersion?.topic_type === TopicVersionType.scorm
        ? 'network-only'
        : 'cache-only',
    // we want onCompleted call even on refetch
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      setIsLoading(false)
      if (data.topic) {
        setTopic(data.topic)
      }
    }
  })

  useOpenTopicWithVersionQuery({
    // Do not remove fetchPolicy, it will break merging of publishedElements.
    // There are two calls getting publishedElements, each of them on different topic version,
    // that means each of them is getting different publishedElements content with same ID. This breaks merging in Apollo (and it's not a bug).
    // Apollo merges together publishedElements from currentTopicVersionAsEditor and topicVersion namespaces.
    // TODO: Would be great to fix this to be able to remove fetchPolicy. BE should not return non-unique IDs in published elements snapshots.
    fetchPolicy: 'network-only',
    skip: mode !== TopicPageMode.VersionView,
    variables: {
      topicId,
      topicVersion: params.topicVersion
    },
    onCompleted: (data) => {
      setIsLoading(false)
      if (data?.topic && data.topic.__typename === 'TopicModelAsEditor') {
        if (
          data.topic.currentTopicVersion?.id ===
          data.topic.topicVersions?.[0]?.id
        ) {
          navigateTo(linkBuilder.topicUrl(topicId))
        }
        setTopic(data.topic)
      }
    }
  })

  const { refetch: refetchTopicEnrolmentPathway } =
    useOpenTopicEnrolmentThroughPathwayQuery({
      skip: mode !== TopicPageMode.PathwayView,
      variables: {
        topicId,
        pathwayId,
        restartEnrolment: false
      },
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-only',
      // we want onCompleted call even on refetch
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        setIsLoading(false)
        if (data?.pathway?.topic) {
          setPathway(data?.pathway)
          setTopic(data.pathway.topic)
          setNextInPathway(data?.pathway?.nextTopicAfter)
        }
      }
    })

  useTopicDraftVersionQuery({
    skip: mode !== TopicPageMode.Preview && mode !== TopicPageMode.Gradebook,
    variables: {
      topicId
    },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
    // we want onCompleted call even on refetch
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      setIsLoading(false)
      if (data?.topic) {
        setTopic(data.topic as any)
      }
    }
  })

  useEffect(() => {
    // Campaigns links click tracking
    if (mode !== TopicPageMode.WorkspaceView) {
      // Other modes are ignored. Topic links with scheduled_action param are generated only for regular
      // WorkspaceView using /topic/:topicId route
      return
    }
    const parsedQueryString = queryString.parse(location.search)
    const scheduledActionId = getScheduledActionIdFromURL(parsedQueryString)
    if (scheduledActionId) {
      // This topic was opened from Topic share action email sent from campaign.
      openedFromCampaign({
        variables: {
          id: topicId,
          scheduledActionId
        }
      })

      // Remove scheduled_action param from current history state (means from current URL in browser)
      delete parsedQueryString.scheduled_action
      history.replace(
        `${linkBuilder.topicUrl(topicId)}?${queryString.stringify(
          parsedQueryString
        )}`
      )
    }
  }, [topicId, pathwayId])

  const resetEnrolment = async () => {
    if (mode === TopicPageMode.PathwayView) {
      refetchTopicEnrolmentPathway({
        topicId,
        pathwayId,
        restartEnrolment: true
      })
    } else {
      refetchTopicEnrolment({
        topicId,
        restartEnrolment: true
      })
    }
  }

  const updateTopicProgress = async (elementId) => {
    if (!topic?.enrolmentOrSelfEnrol) {
      return
    }

    if (!topic.enrolmentOrSelfEnrol.completion_date) {
      // we can safely ignore if the enrolment is already complete
      const { data } = await updateEnrolmentProgress({
        variables: {
          topicId: topic?.id,
          elementId
        }
      })

      const updateProgress = data?.topic?.lastEnrolment?.updateProgress

      if (topic?.enrolmentOrSelfEnrol) {
        setTopic({
          ...topic,
          enrolmentOrSelfEnrol: {
            ...topic.enrolmentOrSelfEnrol,
            ...updateProgress
          }
        })
      }
    }
  }

  const reloadEnrolment = async (updatedEnrol) => {
    if (!topic?.enrolmentOrSelfEnrol) return

    const lastEnrolmentResult = await apolloClientPrivate.query({
      fetchPolicy: 'network-only',
      query: LastEnrolmentForUserDocument,
      variables: {
        topicId: topic?.id
      }
    })

    const {
      auto_correct,
      completion_date,
      progress,
      max_visited_element,
      isCpdEntry
    } = lastEnrolmentResult.data.topic.lastEnrolment

    setTopic({
      ...topic,
      enrolmentOrSelfEnrol: {
        ...topic.enrolmentOrSelfEnrol,
        quizResponses: updatedEnrol.quizResponses,
        auto_correct,
        completion_date,
        progress,
        max_visited_element,
        isCpdEntry
      }
    })
  }

  const saveFreeQuizResponse = async (
    elementId: number,
    response: string
  ): Promise<IQuizResponse> => {
    if (!topic) {
      throw new Error(t`Topic is not available`)
    }

    const { data } = await addFreeQuizResponse({
      variables: {
        topicId: topic?.id,
        elementId,
        response
      }
    })

    const quizResponse = data?.topic?.lastEnrolment
      ?.addFreeQuizResponse as IQuizResponse

    const updatedEnrol = addQuizResponseMethod(quizResponse)

    // Refetch current auto_correct after updating progress in previous mutation
    await reloadEnrolment(updatedEnrol)

    return quizResponse
  }

  const saveQuizResponse = async (
    elementId: number,
    optionResponses: IOptionResponse[]
  ): Promise<IQuizResponse | null> => {
    if (!topic) {
      return null
    }
    const { data } = await addQuizResponse({
      variables: {
        topicId: topic.id,
        elementId,
        optionResponses
      }
    })

    const quizResponse = data?.topic?.lastEnrolment
      ?.addQuizResponse as IQuizResponse

    const updatedEnrol = addQuizResponseMethod(quizResponse)

    // Refetch current auto_correct after updating progress in previous mutation
    await reloadEnrolment(updatedEnrol)

    return quizResponse
  }

  const addQuizResponseMethod = (quizResponse: IQuizResponse) => {
    if (!topic?.enrolmentOrSelfEnrol) {
      return null
    }
    const quizResponses = [...topic.enrolmentOrSelfEnrol.quizResponses]
    quizResponses.push(quizResponse)

    if (topic?.enrolmentOrSelfEnrol) {
      setTopic({
        ...topic,
        enrolmentOrSelfEnrol: {
          ...topic.enrolmentOrSelfEnrol,
          quizResponses
        }
      })
    }

    return {
      ...topic.enrolmentOrSelfEnrol,
      quizResponses
    }
  }

  if (
    !Number.isInteger(topicId) ||
    (pathwayId && !Number.isInteger(pathwayId))
  ) {
    return (
      <>
        <div className="Auth__noAccess">
          <IconBan />
          <h2>
            <Trans>Unable to load resource due to invalid URL</Trans>
          </h2>
          <p>
            <Trans>
              Go back <Link to={staticLinks.home}>home</Link>
            </Trans>
          </p>
        </div>
      </>
    )
  }

  if (isLoading) {
    return <FullBlockSpinner />
  }

  if (!topic) {
    return (
      <>
        <div className="Auth__noAccess">
          <IconBan />
          <h2>
            <Trans>You don't have access to this topic</Trans>
          </h2>
          <p>
            <Trans>
              Go back <Link to={staticLinks.home}>home</Link>
            </Trans>
          </p>
        </div>
      </>
    )
  }

  if (topic.__typename !== 'TopicModelAsEditor' && topic.deleted_at) {
    return <NotFound message={t`This resource was archived.`} />
  }

  const topicVersion =
    topic?.draftVersion ||
    topic?.enrolmentOrSelfEnrol?.topicVersion ||
    topic?.topicVersions?.[0]

  return (
    <TopicDetailContext.Provider
      value={{
        updateTopicProgress,
        saveQuizResponse,
        saveFreeQuizResponse,
        resetEnrolment,
        topicVersion,
        isLoading,
        topic,
        pathway,
        nextInPathway,
        mode
      }}
    >
      <StudentTopicDetail />
    </TopicDetailContext.Provider>
  )
}

const verifyTopicVersionParam = (topicVersion: string): boolean => {
  let [majorVersion, minorVersion] = topicVersion.split('.')
  if (Number.isInteger(+majorVersion) && Number.isInteger(+minorVersion)) {
    return true
  }
  return false
}

export default TopicDetailPage
